import { TRoadblockCaseData, TRoadblockConfiguration } from "../roadblockTypesAndCodecs";
import { fact, formatEvidenceDate } from "../evidence/evidenceHelpers";
import { CaseLedgerType_displayString, isRetentionType } from "../../codecs/CaseLedger";
import { formatCurrencyFromPence } from "../../../shared/src/util/numberFormatting";
import { TRoadblockEvidenceLink } from "../../codecs/form/RoadblockForm";

const postCompletionLink: TRoadblockEvidenceLink = { block: "case_details", section: "post_completion" };
const clientFundsLink: TRoadblockEvidenceLink = { block: "case_details", section: "client_funds" };
const outgoingPaymentsLink: TRoadblockEvidenceLink = { block: "case_details", section: "outgoing_payments" };
const caseManagementLink: TRoadblockEvidenceLink = { block: "case_details", section: "case_management" };
const retentionsLink: TRoadblockEvidenceLink = { block: "case_details", section: "retentions" };

const activeLedgerEntries = (c: TRoadblockCaseData) =>
    c.case_ledger_entries.filter((l) => l.deleted_at === null);

const everyActiveEntryPaid = (c: TRoadblockCaseData) => {
    const entries = activeLedgerEntries(c);
    return entries.length > 0
        && entries.every((l) => l.paid_at !== null || l.user_says_paid !== null);
};

const activeRetentions = (c: TRoadblockCaseData) =>
    activeLedgerEntries(c).filter((l) => isRetentionType(l.type));

const everyPaidEntryHasSlipInCashroom = (c: TRoadblockCaseData) => {
    const paidEntries = activeLedgerEntries(c).filter(
        (l) => l.paid_at !== null || l.user_says_paid !== null
    );
    return paidEntries.length > 0 && paidEntries.every((l) => l.slip_added_to_cashroom !== null);
};

/**
 * PostCompletion is the after-completion housekeeping roadblock. It tracks the
 * HMLR registration sub-flow (submit → resolve requisitions/cancellation →
 * complete registration) and the post-completion finance work (cashroom slips,
 * payouts, ledger finalisation, Sail Legal invoice paid, post handling).
 *
 * Only creates after the case is recorded as completed (`cases.has_completed`).
 * PostCompletion is the one roadblock that survives case lock-down
 * (`final_closing_review_completed` set): the auto-suspend rule in
 * workOutRoadblocksToBeStateForCase exempts it, because a still-open registration
 * or payout on a locked case is real outstanding work, not noise (ADR 0009).
 * On locked cases its items are only re-derived on real case touches — the dormant
 * sweep deliberately stops picking a locked case up once every other roadblock is
 * terminal.
 */
const registersWithLandRegistry = (c: TRoadblockCaseData) =>
    c.case.transaction_type === "purchase"
    || c.case.transaction_type === "transfer"
    || c.case.transaction_type === "remortgage";

const SIGNED_TRANSFER_DOCUMENT_TYPES = [
    "signed_tr1",
    "signed_tp1",
    "signed_tr2",
    "signed_tp2",
    "signed_as1",
    "signed_as3",
] as const;

const retentionOutstandingDescription = (reason: string | null, chaseFrom: string | null): string => {
    const what = reason !== null && reason.trim() !== "" ? reason.trim() : "No reason recorded";
    return chaseFrom === null
        ? `${what} — no chase date`
        : `${what} — chase from ${formatEvidenceDate(chaseFrom)}`;
};

export const PostCompletion: TRoadblockConfiguration = {
    type: "post_completion",
    display_name: "Post-Completion",
    only_create_if_resolved: [],
    requires_authoriser_signoff: false,
    blocks_exchange: false,
    create: {
        create_condition_description: "Cases that have completed (cases.has_completed IS NOT NULL)",
        predicate: (c) => c.case.has_completed !== null,
    },
    checklist: [
        // --- HMLR registration: pre-submission readiness ---
        // These gate the "Submit to Land Registry" action: the application can't be
        // assembled/submitted until the fee is known and the executed deeds are in.
        // (The remaining data-gap answers — disclosable overriding interests, charge MD
        // reference, discharge submission type — are captured on the submission preview.)
        {
            type: "registration_signed_transfer_received",
            is_resolved_by_short_description: "Signed transfer/assent deed received",
            create: {
                create_condition_description: "Purchase / transfer cases (the transfer of the legal estate; a remortgage has no transfer)",
                predicate: (c) =>
                    c.case.transaction_type === "purchase" || c.case.transaction_type === "transfer",
            },
            resolve: {
                is_resolved_by_long_description: `A signed transfer or assent deed (TR1/TP1/TR2/TP2/AS1/AS3) is on file ready to lodge.`,
                predicate: (c) => c.cases_documents.some((d) => SIGNED_TRANSFER_DOCUMENT_TYPES.some((t) => t === d.type)),
                evidence: (c) => {
                    const received = c.cases_documents.some((d) => SIGNED_TRANSFER_DOCUMENT_TYPES.some((t) => t === d.type));
                    return [fact("Signed transfer/assent deed", received ? "On file" : "Not received", received, postCompletionLink)];
                },
            },
        },
        {
            type: "registration_signed_charge_received",
            is_resolved_by_short_description: "Signed charge (mortgage) deed received",
            create: {
                create_condition_description: "Cases with a new mortgage advance (a charge to register), unless the lender has confirmed no new mortgage deed is required",
                predicate: (c) =>
                    c.case.mortgage_offer_advance_amount_pence !== null
                    && c.case.mortgage_offer_lender_requires_a_new_mortgage_deed !== false,
            },
            resolve: {
                is_resolved_by_long_description: `A signed mortgage deed is on file ready to lodge with the charge.`,
                predicate: (c) => c.cases_documents.some((d) => d.type === "signed_mortgage_deed"),
                evidence: (c) => {
                    const received = c.cases_documents.some((d) => d.type === "signed_mortgage_deed");
                    return [fact("Signed mortgage deed", received ? "On file" : "Not received", received, postCompletionLink)];
                },
            },
        },
        // --- HMLR registration: post-submission lifecycle (driven by the notification poller) ---
        {
            type: "registration_submitted",
            is_resolved_by_short_description: "Registration submitted to Land Registry",
            create: {
                create_condition_description: "Cases where Sail Legal is the registering party (purchase / transfer / remortgage)",
                predicate: registersWithLandRegistry,
            },
            resolve: {
                is_resolved_by_long_description: `Recording the date the case's Land Registry application was submitted.`,
                predicate: (c) => c.case.registration_submitted !== null,
                evidence: (c) => [
                    fact(
                        "Submitted to Land Registry",
                        c.case.registration_submitted !== null ? formatEvidenceDate(c.case.registration_submitted) : "Not submitted",
                        c.case.registration_submitted !== null,
                        postCompletionLink,
                    ),
                ],
            },
        },
        {
            type: "requisitions_resolved",
            is_resolved_by_short_description: "Requisitions resolved",
            create: {
                create_condition_description: "Cases where Land Registry has raised requisitions",
                predicate: (c) => c.case.registration_requisitions_exist !== null,
            },
            resolve: {
                is_resolved_by_long_description: `Recording the date the Land Registry requisitions were resolved.`,
                predicate: (c) => c.case.registration_requisitions_resolved !== null,
                evidence: (c) => [
                    fact(
                        "Requisitions raised",
                        c.case.registration_requisitions_exist !== null ? formatEvidenceDate(c.case.registration_requisitions_exist) : "Not recorded",
                        c.case.registration_requisitions_exist !== null,
                        postCompletionLink,
                    ),
                    fact(
                        "Requisitions resolved",
                        c.case.registration_requisitions_resolved !== null ? formatEvidenceDate(c.case.registration_requisitions_resolved) : "Not resolved",
                        c.case.registration_requisitions_resolved !== null,
                        postCompletionLink,
                    ),
                ],
            },
        },
        {
            type: "cancellation_resolved",
            is_resolved_by_short_description: "Registration cancellation resolved",
            create: {
                create_condition_description: "Cases where Land Registry has cancelled the submission",
                predicate: (c) => c.case.registration_submission_cancelled !== null,
            },
            resolve: {
                is_resolved_by_long_description: `Recording the date the case was resubmitted to Land Registry after cancellation.`,
                predicate: (c) => c.case.registration_resubmitted !== null,
                evidence: (c) => [
                    fact(
                        "Submission cancelled",
                        c.case.registration_submission_cancelled !== null ? formatEvidenceDate(c.case.registration_submission_cancelled) : "Not recorded",
                        c.case.registration_submission_cancelled !== null,
                        postCompletionLink,
                    ),
                    fact(
                        "Resubmitted",
                        c.case.registration_resubmitted !== null ? formatEvidenceDate(c.case.registration_resubmitted) : "Not resubmitted",
                        c.case.registration_resubmitted !== null,
                        postCompletionLink,
                    ),
                ],
            },
        },
        {
            type: "registration_complete",
            is_resolved_by_short_description: "Registration complete",
            create: {
                create_condition_description: "Cases where Sail Legal is the registering party (purchase / transfer / remortgage)",
                predicate: (c) =>
                    c.case.transaction_type === "purchase"
                    || c.case.transaction_type === "transfer"
                    || c.case.transaction_type === "remortgage",
            },
            resolve: {
                is_resolved_by_long_description: `Recording the date the Land Registry confirmed the registration as complete.`,
                predicate: (c) => c.case.registration_complete !== null,
                evidence: (c) => [
                    fact(
                        "Registration complete",
                        c.case.registration_complete !== null ? formatEvidenceDate(c.case.registration_complete) : "Not complete",
                        c.case.registration_complete !== null,
                        postCompletionLink,
                    ),
                ],
            },
        },

        // --- Finance & ledger ---
        {
            type: "case_ledger_finalised",
            is_resolved_by_short_description: "Case ledger finalised",
            create: { create_condition_description: "All post-completion cases", predicate: () => true },
            resolve: {
                is_resolved_by_long_description: `Recording the date the case ledger was finalised.`,
                predicate: (c) => c.case.ledger_finalised_at !== null,
                evidence: (c) => [
                    fact(
                        "Ledger finalised",
                        c.case.ledger_finalised_at !== null ? formatEvidenceDate(c.case.ledger_finalised_at) : "Not finalised",
                        c.case.ledger_finalised_at !== null,
                        clientFundsLink,
                    ),
                ],
            },
        },
        {
            type: "payments_due_chased",
            is_resolved_by_short_description: "Payments due chased",
            create: {
                create_condition_description: "Disabled — no deterministic 'chased' signal yet (see CompletionReady for funds-in items)",
                predicate: () => false,
            },
            resolve: {
                is_resolved_by_long_description: `All outstanding payments due have been chased with the client.`,
                predicate: () => false,
            },
        },
        {
            type: "slip_added_to_cashroom",
            is_resolved_by_short_description: "Slip added to cashroom for every paid ledger entry",
            create: {
                create_condition_description: "Cases with active ledger entries",
                predicate: (c) => activeLedgerEntries(c).length > 0,
            },
            resolve: {
                is_resolved_by_long_description: `Every paid (paid_at or user_says_paid) active ledger entry having slip_added_to_cashroom recorded.`,
                predicate: (c) => everyPaidEntryHasSlipInCashroom(c),
                evidence: (c) =>
                    activeLedgerEntries(c)
                        .filter((l) => l.paid_at !== null || l.user_says_paid !== null)
                        .map((l) =>
                            fact(
                                CaseLedgerType_displayString(l.type),
                                l.slip_added_to_cashroom !== null ? `Slip added ${formatEvidenceDate(l.slip_added_to_cashroom)}` : "No slip",
                                l.slip_added_to_cashroom !== null,
                                clientFundsLink,
                            )
                        ),
            },
        },
        {
            type: "ledger_items_paid_out",
            is_resolved_by_short_description: "All ledger items paid out",
            create: {
                create_condition_description: "Cases with active ledger entries",
                predicate: (c) => activeLedgerEntries(c).length > 0,
            },
            resolve: {
                is_resolved_by_long_description: `Every non-deleted ledger entry having either paid_at or user_says_paid recorded.`,
                predicate: (c) => everyActiveEntryPaid(c),
                evidence: (c) =>
                    activeLedgerEntries(c).map((l) => {
                        const paidAt = l.paid_at ?? l.user_says_paid;
                        return fact(
                            CaseLedgerType_displayString(l.type),
                            paidAt !== null ? `Paid ${formatEvidenceDate(paidAt)}` : "Not paid",
                            paidAt !== null,
                            outgoingPaymentsLink,
                        );
                    }),
            },
        },
        {
            type: "retention_settled",
            is_resolved_by_short_description: "Retentions settled",
            create: {
                create_condition_description: "Cases with a retention on the ledger (any money_retained_by_* entry)",
                predicate: (c) => activeRetentions(c).length > 0,
            },
            resolve: {
                is_resolved_by_long_description: `Every retention on the case being marked as settled. Settlement is recorded separately from payment because on a retention held by the other side, 'payment sent' is the moment the retention is taken, not released.`,
                predicate: (c) => activeRetentions(c).every((l) => l.retention_settled_at !== null),
                evidence: (c) =>
                    activeRetentions(c).map((l) =>
                        fact(
                            `${CaseLedgerType_displayString(l.type)} ${formatCurrencyFromPence(l.vat_excluded_amount_gbp_pence + l.vat_amount_gbp_pence)}`,
                            l.retention_settled_at !== null
                                ? `Settled ${formatEvidenceDate(l.retention_settled_at)}`
                                : retentionOutstandingDescription(l.retention_reason, l.retention_chase_from),
                            l.retention_settled_at !== null,
                            retentionsLink,
                        )
                    ),
            },
        },
        {
            type: "sail_legal_invoice_paid",
            is_resolved_by_short_description: "Sail Legal invoice paid",
            create: { create_condition_description: "All post-completion cases", predicate: () => true },
            resolve: {
                is_resolved_by_long_description: `Recording the date the Sail Legal invoice was paid from client-account funds.`,
                predicate: (c) => c.case.funds_transferred_to_office_account_for_sail_legal_invoice !== null,
                evidence: (c) => [
                    fact(
                        "Sail Legal invoice paid",
                        c.case.funds_transferred_to_office_account_for_sail_legal_invoice !== null
                            ? formatEvidenceDate(c.case.funds_transferred_to_office_account_for_sail_legal_invoice)
                            : "Not paid",
                        c.case.funds_transferred_to_office_account_for_sail_legal_invoice !== null,
                        outgoingPaymentsLink,
                    ),
                ],
            },
        },

        // --- Admin ---
        {
            // Only where a fee is actually owed: has_introducer alone isn't enough.
            // Several introducers are on nil-fee terms, and has_introducer is a plain
            // boolean set independently of introducer_id, so it can be true with nobody
            // linked to pay.
            type: "referral_fee_paid",
            is_resolved_by_short_description: "Introducer referral fee paid",
            create: {
                create_condition_description: "Cases with a linked introducer and a non-zero referral fee",
                predicate: (c) =>
                    c.case.has_introducer
                    && c.case.introducer_id !== null
                    && c.case.introducer_referral_fee_gbp_pence !== null
                    && c.case.introducer_referral_fee_gbp_pence > 0,
            },
            resolve: {
                is_resolved_by_long_description: `Recording the date the introducer's referral fee was paid.`,
                predicate: (c) => c.case.referral_fee_paid !== null,
                evidence: (c) => [
                    fact(
                        "Referral fee paid",
                        c.case.referral_fee_paid !== null ? formatEvidenceDate(c.case.referral_fee_paid) : "Not paid",
                        c.case.referral_fee_paid !== null,
                        caseManagementLink,
                    ),
                ],
            },
        },
        {
            type: "post_forwarded_or_shredded",
            is_resolved_by_short_description: "Post forwarded or shredded",
            create: { create_condition_description: "All post-completion cases", predicate: () => true },
            resolve: {
                is_resolved_by_long_description: `Recording that any remaining post on the case has been forwarded or shredded.`,
                predicate: (c) => c.case.post_forwarded_or_shredded !== null,
                evidence: (c) => [
                    fact(
                        "Post forwarded or shredded",
                        c.case.post_forwarded_or_shredded !== null ? formatEvidenceDate(c.case.post_forwarded_or_shredded) : "Not done",
                        c.case.post_forwarded_or_shredded !== null,
                        caseManagementLink,
                    ),
                ],
            },
        },
    ],
};
